import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  Shield, Flag, EyeOff, Eye, Trash2, RotateCcw, Ban,
  Clock, AlertTriangle, FileText, User as UserIcon, ImageIcon, MessageSquare,
  ShieldAlert, BadgeCheck, Check, X as XIcon, Mail, MessageSquarePlus, Gift, Megaphone, Trophy, MailWarning, AlertOctagon, Link as LinkIcon, Loader2,
} from "lucide-react";

const TARGET_ICONS = {
  user: UserIcon, image: ImageIcon, video: ImageIcon, novelle: FileText,
  thread: MessageSquare, post: MessageSquare, message: MessageSquare,
  guestbook: MessageSquare, comment: MessageSquare,
  profile_post: FileText, profile_post_comment: MessageSquare,
};

function relTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "nå nettopp";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} t`;
  return `${Math.floor(diff / 86400)} d`;
}

// ============================================================
// HOVEDSIDE
// ============================================================
export default function AdminModerasjon() {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const initialTab = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("tab") || "queue";
    } catch { return "queue"; }
  })();
  const [tab, setTab] = useState(initialTab);
  const [stats, setStats] = useState(null);
  // Synk tab til URL slik at refresh beholder valgt fane
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("tab") !== tab) {
        url.searchParams.set("tab", tab);
        window.history.replaceState({}, "", url.toString());
      }
    } catch { /* URL update non-critical */ }
  }, [tab]);
  useEffect(() => {
    if (!user || !["admin", "owner"].includes(user.role)) return;
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user]);
  if (!loading && (!user || !["admin", "owner"].includes(user.role))) {
    return <Navigate to="/" replace />;
  }

  return (
    <Layout withSidebar={false}>
      <header className="mb-8" data-testid="moderation-header">
        <div className="flex items-center gap-3 mb-3">
          <Shield className="h-5 w-5 text-[var(--magenta)]" />
          <div className="font-meta text-[var(--magenta)]">ADMIN · MODERASJON</div>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-fraunces italic text-4xl sm:text-5xl text-[var(--cream)] leading-tight">
            Moderasjons­panel.
          </h1>
          <Link
            to="/admin/konkurranser/ny"
            data-testid="admin-new-contest-btn"
            className="px-4 py-2 bg-[var(--magenta)] text-black font-meta inline-flex items-center gap-2 shrink-0"
          >
            <Trophy className="h-4 w-4" /> {t("admin.new_contest_btn")}
          </Link>
        </div>
      </header>

      <StatsBar stats={stats} />

      <nav className="border-y border-white/10 my-8">
        <div className="flex gap-2 overflow-x-auto">
          {[
            { k: "queue",        label: "Rapporter",              icon: Flag,             badge: stats?.open_reports },
            { k: "csam",         label: "CSAM",                    icon: ShieldAlert,      badge: stats?.csam_pending },
            { k: "verif",        label: "Selger-verifisering",     icon: BadgeCheck,       badge: stats?.seller_verif_pending },
            { k: "seller-links", label: "Selger-lenker",           icon: LinkIcon,         badge: stats?.seller_links_pending },
            { k: "creator-apps", label: "Skaper-søknader",         icon: UserIcon,         badge: stats?.creator_apps_pending },
            { k: "coaching",     label: "Coaching-forespørsler",   icon: Mail,             badge: stats?.coaching_new },
            { k: "feedback",     label: "Tilbakemeldinger",        icon: MessageSquarePlus, badge: stats?.feedback_new },
            { k: "promo",        label: "Promo-koder",             icon: Gift },
            { k: "sysmsg",       label: "System-meldinger",        icon: Megaphone },
            { k: "users",        label: "Suspendér bruker",        icon: Ban },
            { k: "verify-email", label: "Force-verifiser e-post",  icon: BadgeCheck },
            { k: "unverified",   label: "Ikke verifisert",         icon: MailWarning },
            { k: "failed-signups", label: "Mislykkede registreringer", icon: AlertOctagon },
            { k: "audit",        label: "Audit-logg",              icon: Clock },
          ].map(({ k, label, icon: Icon, badge }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`mod-tab-${k}`}
              className={`flex items-center gap-2 px-4 py-3 font-meta transition-colors whitespace-nowrap ${
                tab === k
                  ? "text-[var(--magenta)] border-b-2 border-[var(--magenta)]"
                  : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
              {Number(badge) > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold leading-none rounded-full bg-[var(--magenta)] text-black"
                  data-testid={`mod-tab-badge-${k}`}
                >
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {tab === "queue" && <ReportQueue />}
      {tab === "csam" && <CSAMQueue />}
      {tab === "verif" && <SellerVerificationQueue />}
      {tab === "seller-links" && <SellerLinksQueue />}
      {tab === "creator-apps" && <CreatorApplicationQueue />}
      {tab === "coaching" && <CoachingInquiryQueue />}
      {tab === "feedback" && <FeedbackQueue />}
      {tab === "promo" && <PromoCodeAdmin />}
      {tab === "sysmsg" && <SystemMessageAdmin />}
      {tab === "users" && <UserSuspendTool />}
      {tab === "verify-email" && <ForceVerifyEmailTool />}
      {tab === "unverified" && <UnverifiedUsersList />}
      {tab === "failed-signups" && <FailedSignupsList />}
      {tab === "audit" && <AuditLog />}
    </Layout>
  );
}

// ============================================================
// SKAPER-SØKNADER
// ============================================================
function CreatorApplicationQueue() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/admin/creator-applications?status=${status}`)
      .then(r => setRows(r.data?.results || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const [codeModal, setCodeModal] = useState(null); // { code, username, message }

  const sendCode = async (id, username) => {
    setBusy(id);
    try {
      const r = await api.post(`/admin/creator-applications/${id}/send-code`);
      const code = r.data?.code;
      const message = `Hei ${username}! Velkommen til Norsk Erotikk. Din verifiseringskode er ${code} – skriv den inn på norskerotikk.no/bli-skaper for å aktivere skapersiden din.`;
      try { await navigator.clipboard.writeText(message); } catch (_) {}
      setCodeModal({ code, username, message });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const approve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/creator-applications/${id}/approve`);
      toast.success("Godkjent – brukeren er nå skaper.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const reject = async (id) => {
    if (!rejectReason.trim()) return toast.error("Skriv en grunn for avvisning");
    setBusy(id);
    try {
      await api.post(`/admin/creator-applications/${id}/reject`, { reason: rejectReason.trim() });
      toast.success("Avvist – brukeren får varsel.");
      setRejectId(null); setRejectReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-creator-apps">
      <div className="flex items-center gap-3 mb-6">
        <div className="font-meta">Filter:</div>
        {["pending", "code_sent", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatus(s)}
            className={`font-meta px-3 py-1.5 transition-colors ${
              status === s ? "text-[var(--magenta)] border-b border-[var(--magenta)]" : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}
            data-testid={`creator-app-filter-${s}`}>
            {s === "pending" ? "Til behandling" : s === "code_sent" ? "Venter på bruker" : s === "approved" ? "Godkjent" : "Avvist"}
          </button>
        ))}
        <button onClick={load} className="ml-auto font-meta text-[var(--cream-soft)] hover:text-[var(--cream)]">
          <RotateCcw className="h-3.5 w-3.5 inline mr-1" /> Oppdater
        </button>
      </div>

      {loading ? (
        <div className="font-meta text-[var(--cream-soft)]">Laster…</div>
      ) : rows.length === 0 ? (
        <div className="editorial-card p-8 text-center">
          <UserIcon className="h-6 w-6 text-[var(--cream-faint)] mx-auto mb-2" />
          <p className="font-fraunces italic text-[var(--cream-soft)]">
            {status === "pending" ? "Ingen søknader til behandling." : "Ingen treff."}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {rows.map((r) => (
            <CreatorAppCard key={r.id} app={r} busy={busy === r.id}
              onSendCode={() => sendCode(r.id, r.username)}
              onApprove={() => approve(r.id)}
              onRejectOpen={() => { setRejectId(r.id); setRejectReason(""); }}
              showActions={r.status === "pending" || r.status === "code_sent"} />
          ))}
        </div>
      )}

      {rejectId && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[var(--noir-elev)] border border-white/15 p-6 max-w-md w-full">
            <h3 className="font-fraunces italic text-xl text-[var(--cream)] mb-4">Avvis søknad</h3>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              maxLength={500} rows={4}
              placeholder="F.eks: Profilene oppgitt er privat og admin kan ikke verifisere identiteten."
              className="input-editorial w-full mb-4" data-testid="creator-reject-reason" />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setRejectId(null)} className="btn-ghost">Avbryt</button>
              <button onClick={() => reject(rejectId)} disabled={busy === rejectId || !rejectReason.trim()}
                className="btn-magenta" data-testid="creator-reject-confirm">
                {busy === rejectId ? "Sender…" : "Avvis søknad"}
              </button>
            </div>
          </div>
        </div>
      )}

      {codeModal && (
        <CodeSentModal data={codeModal} onClose={() => setCodeModal(null)} />
      )}
    </div>
  );
}

function CodeSentModal({ data, onClose }) {
  const [copiedField, setCopiedField] = useState(null);
  const copy = async (txt, field) => {
    try {
      await navigator.clipboard.writeText(txt);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1800);
    } catch (_) {
      toast.error("Kunne ikke kopiere – marker teksten manuelt");
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="code-sent-modal">
      <div className="bg-[var(--noir-elev)] border-l-2 border-[var(--magenta)] p-7 max-w-xl w-full">
        <div className="font-meta text-[var(--magenta)] mb-2">KODE GENERERT</div>
        <h3 className="font-fraunces italic text-2xl text-[var(--cream)] mb-5">
          Send denne meldingen til @{data.username} via DM på den eksterne profilen.
        </h3>

        {/* Kode-boksen */}
        <div className="mb-5">
          <div className="font-meta text-[var(--cream-soft)] mb-2">Kode (kun tallene)</div>
          <div className="flex gap-2">
            <code className="flex-1 bg-[var(--noir-2)] border border-white/10 px-4 py-3 font-mono text-2xl tracking-[0.4em] text-center text-[var(--gold)]">
              {data.code}
            </code>
            <button onClick={() => copy(data.code, "code")}
              className="btn-ghost px-4 whitespace-nowrap"
              data-testid="copy-code-only">
              {copiedField === "code" ? "✓ Kopiert" : "Kopier kode"}
            </button>
          </div>
        </div>

        {/* Mal-meldingen */}
        <div className="mb-5">
          <div className="font-meta text-[var(--cream-soft)] mb-2">Ferdig DM-mal (allerede kopiert)</div>
          <textarea
            readOnly
            value={data.message}
            rows={4}
            className="input-editorial w-full font-manrope text-sm"
            onClick={(e) => e.target.select()}
            data-testid="dm-template-text"
          />
          <button onClick={() => copy(data.message, "message")}
            className="btn-magenta mt-2 w-full" data-testid="copy-dm-template">
            {copiedField === "message" ? "✓ Kopiert til utklippstavle" : "Kopier hele meldingen igjen"}
          </button>
        </div>

        <div className="border-t border-white/10 pt-4 text-xs text-[var(--cream-faint)] leading-relaxed">
          Søkeren har 5 forsøk på å skrive inn koden riktig. Etter det må du sende ny kode.
        </div>

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="btn-ghost" data-testid="code-modal-close">
            Lukk
          </button>
        </div>
      </div>
    </div>
  );
}

function CreatorAppCard({ app, busy, onSendCode, onApprove, onRejectOpen, showActions }) {
  const submitted = app.created_at ? new Date(app.created_at).toLocaleString("nb-NO") : "—";
  const codeSent = app.status === "code_sent";
  return (
    <div className="border border-white/10 p-5" data-testid={`creator-app-${app.id}`}>
      <header className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-fraunces italic text-xl text-[var(--cream)]">@{app.username}</h3>
            <StatusBadge status={app.status} />
          </div>
          <div className="font-meta text-[10px]">Sendt: {submitted} · ID: {app.id.slice(0, 8)}</div>
          {codeSent && app.verification_code && (
            <div className="font-meta text-[10px] text-[var(--gold)] mt-1">
              Aktiv kode: <span className="font-mono">{app.verification_code}</span>
              {" · "}
              {app.code_attempts > 0 && `${app.code_attempts}/5 feilforsøk`}
            </div>
          )}
        </div>
        {showActions && (
          <div className="flex gap-2 flex-wrap">
            <button onClick={onRejectOpen} disabled={busy}
              className="btn-ghost flex items-center gap-1" data-testid={`creator-reject-${app.id}`}>
              <XIcon className="h-3.5 w-3.5" /> Avvis
            </button>
            <button onClick={onSendCode} disabled={busy}
              className="btn-ghost flex items-center gap-1 border border-[var(--gold)]/40 text-[var(--gold)]"
              data-testid={`creator-send-code-${app.id}`}>
              {codeSent ? "Ny kode" : "Send kode"}
            </button>
            <button onClick={onApprove} disabled={busy}
              className="btn-magenta flex items-center gap-1" data-testid={`creator-approve-${app.id}`}>
              <Check className="h-3.5 w-3.5" /> {busy ? "Godkjenner…" : "Direkte godkjenn"}
            </button>
          </div>
        )}
      </header>

      {app.reason && app.reason.trim() && (
        <div className="mb-4">
          <div className="font-meta mb-2">Begrunnelse</div>
          <p className="font-manrope text-sm text-[var(--cream)] leading-relaxed whitespace-pre-wrap">{app.reason}</p>
        </div>
      )}

      <div className="border-t border-white/10 pt-4">
        <div className="font-meta mb-3">Eksterne profiler ({(app.links || []).length})</div>
        <div className="space-y-2">
          {(app.links || []).map((l, i) => (
            <div key={i} className="flex flex-wrap items-center gap-3 text-sm">
              <span className={`font-meta text-[10px] px-2 py-0.5 ${
                l.is_strong ? "bg-emerald-500/15 text-emerald-400" : "bg-[var(--cream-faint)]/15 text-[var(--cream-faint)]"
              }`}>
                {l.platform_label || l.platform}{l.is_strong ? " ✓ ID" : ""}
              </span>
              <a href={l.url} target="_blank" rel="noreferrer"
                className="text-[var(--magenta)] underline-offset-4 hover:underline flex items-center gap-1 break-all">
                {l.url}
              </a>
              {l.open_dm && (
                <span className="font-meta text-[10px] text-emerald-400">DM ÅPEN</span>
              )}
            </div>
          ))}
          {(!app.links || app.links.length === 0) && app.example_links && (
            <pre className="font-manrope text-sm text-[var(--cream-soft)] whitespace-pre-wrap">{app.example_links}</pre>
          )}
        </div>
      </div>

      {app.status === "rejected" && app.reject_reason && (
        <div className="border-t border-white/10 pt-4 mt-4">
          <div className="font-meta text-red-400 mb-1">Avvist grunn</div>
          <p className="font-manrope text-sm text-red-400">{app.reject_reason}</p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// SELGER-VERIFISERINGS-KØ
// ============================================================
function SellerLinksQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/seller-links/pending");
      setItems(r.data.items || []);
    } catch {
      toast.error("Kunne ikke laste");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const act = async (userId, linkId, action) => {
    setBusy(`${userId}-${linkId}`);
    try {
      if (action === "reject") {
        const reason = window.prompt("Avvisning-grunn (vises til selger):");
        if (!reason) return;
        await api.post(`/admin/seller-links/${userId}/${linkId}/reject`, { reason });
      } else {
        await api.post(`/admin/seller-links/${userId}/${linkId}/approve`);
      }
      toast.success(action === "approve" ? "Godkjent" : "Avvist");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feilet");
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="p-6 text-center text-[var(--cream-soft)]"><Loader2 className="animate-spin inline mr-2 h-4 w-4" />Laster...</div>;
  if (!items.length) return <div className="p-6 text-center text-[var(--cream-faint)] italic">Ingen pending selger-lenker.</div>;

  return (
    <div className="space-y-3" data-testid="admin-seller-links-list">
      {items.map(({ user_id, username, display_name, link }) => (
        <div key={link.id} className="panel-card p-4" data-testid={`admin-seller-link-${link.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] uppercase">{display_name || username}</div>
              <div className="font-medium text-[var(--cream)] mt-1">{link.label}</div>
              <a href={link.url} target="_blank" rel="noreferrer nofollow" className="text-xs text-[var(--magenta)] hover:underline block truncate mt-1">
                {link.url}
              </a>
              {link.qr_image_path && <div className="text-xs text-[var(--cream-faint)] mt-1">QR-bilde lastet opp</div>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                disabled={busy === `${user_id}-${link.id}`}
                onClick={() => act(user_id, link.id, "approve")}
                className="px-3 py-1.5 bg-green-700 text-white font-meta text-[10px] tracking-[0.15em] uppercase disabled:opacity-50"
                data-testid={`approve-seller-link-${link.id}`}
              >Godkjenn</button>
              <button
                disabled={busy === `${user_id}-${link.id}`}
                onClick={() => act(user_id, link.id, "reject")}
                className="px-3 py-1.5 bg-red-700 text-white font-meta text-[10px] tracking-[0.15em] uppercase disabled:opacity-50"
                data-testid={`reject-seller-link-${link.id}`}
              >Avvis</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


function SellerVerificationQueue() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);  // verif_id som behandles akkurat nå
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/admin/seller-verifications?status=${status}`)
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/seller-verifications/${id}/review`, { approve: true });
      toast.success("Godkjent – brukeren har fått blå hake.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const reject = async (id) => {
    if (!rejectReason.trim()) return toast.error("Skriv en grunn for avvisning");
    setBusy(id);
    try {
      await api.post(`/admin/seller-verifications/${id}/review`, {
        approve: false, reject_reason: rejectReason.trim(),
      });
      toast.success("Avvist – brukeren får varsel.");
      setRejectId(null);
      setRejectReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-verif-queue">
      <div className="flex items-center gap-3 mb-6">
        <div className="font-meta">Filter:</div>
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatus(s)}
            className={`font-meta px-3 py-1.5 transition-colors ${
              status === s
                ? "text-[var(--magenta)] border-b border-[var(--magenta)]"
                : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}
            data-testid={`verif-filter-${s}`}
          >
            {s === "pending" ? "Til behandling" : s === "approved" ? "Godkjent" : "Avvist"}
          </button>
        ))}
        <button onClick={load} className="ml-auto font-meta text-[var(--cream-soft)] hover:text-[var(--cream)]">
          <RotateCcw className="h-3.5 w-3.5 inline mr-1" /> Oppdater
        </button>
      </div>

      {loading ? (
        <div className="font-meta text-[var(--cream-soft)]">Laster…</div>
      ) : rows.length === 0 ? (
        <div className="editorial-card p-8 text-center">
          <BadgeCheck className="h-6 w-6 text-[var(--cream-faint)] mx-auto mb-2" />
          <p className="font-fraunces italic text-[var(--cream-soft)]">
            {status === "pending" ? "Ingen søknader til behandling." : "Ingen treff."}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {rows.map((r) => (
            <VerificationCard
              key={r.id} v={r} busy={busy === r.id}
              onApprove={() => approve(r.id)}
              onRejectOpen={() => { setRejectId(r.id); setRejectReason(""); }}
              showActions={r.status === "pending"}
            />
          ))}
        </div>
      )}

      {/* Avvis-modal */}
      {rejectId && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="verif-reject-modal">
          <div className="bg-[var(--noir-elev)] border border-white/15 p-6 max-w-md w-full">
            <h3 className="font-fraunces italic text-xl text-[var(--cream)] mb-4">Avvis søknad</h3>
            <p className="font-manrope text-sm text-[var(--cream-soft)] mb-4">
              Grunnen vises til brukeren. Vær konkret så de vet hva som må fikses.
            </p>
            <textarea
              value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              maxLength={500} rows={4}
              placeholder="F.eks: Lappen er ikke synlig på bildet, eller datoen mangler."
              className="input-editorial w-full mb-4" data-testid="verif-reject-reason"
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setRejectId(null)} className="btn-ghost" data-testid="verif-reject-cancel">
                Avbryt
              </button>
              <button onClick={() => reject(rejectId)} disabled={busy === rejectId || !rejectReason.trim()}
                className="btn-magenta" data-testid="verif-reject-confirm">
                {busy === rejectId ? "Sender…" : "Avvis søknad"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VerificationCard({ v, busy, onApprove, onRejectOpen, showActions }) {
  const submitted = v.submitted_at ? new Date(v.submitted_at).toLocaleString("nb-NO") : "—";
  const reviewed = v.reviewed_at ? new Date(v.reviewed_at).toLocaleString("nb-NO") : null;
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  // Privat henter: bruker fetch + credentials="include" (cookies)
  return (
    <div className="border border-white/10 p-5" data-testid={`verif-card-${v.id}`}>
      <header className="flex items-baseline justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-fraunces italic text-xl text-[var(--cream)]">@{v.username}</h3>
            <StatusBadge status={v.status} />
          </div>
          <div className="font-meta text-[10px]">Sendt: {submitted} · ID: {v.id.slice(0, 8)}</div>
          {reviewed && <div className="font-meta text-[10px]">Behandlet: {reviewed} av {v.reviewed_by || "ukjent"}</div>}
        </div>
        {showActions && (
          <div className="flex gap-2 flex-wrap">
            <SendBankIDLinkBtn verif={v} />
            <button onClick={onRejectOpen} disabled={busy}
              className="btn-ghost flex items-center gap-1" data-testid={`verif-reject-${v.id}`}>
              <XIcon className="h-3.5 w-3.5" /> Avvis
            </button>
            <button onClick={onApprove} disabled={busy}
              className="btn-magenta flex items-center gap-1" data-testid={`verif-approve-${v.id}`}>
              <Check className="h-3.5 w-3.5" /> {busy ? "Godkjenner…" : "Godkjenn"}
            </button>
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <PrivatePhoto verifId={v.id} which="front" apiBase={apiBase} label="Bilde 1 — Forfra" />
        <PrivatePhoto verifId={v.id} which="side" apiBase={apiBase} label="Bilde 2 — Fra siden" />
      </div>

      {(v.onlyfans_handle || v.instagram_handle || v.snapchat_handle) && (
        <div className="border-t border-white/10 pt-4 mb-3">
          <div className="font-meta mb-2">Sosiale håndtak</div>
          <div className="flex flex-wrap gap-3 text-sm">
            {v.onlyfans_handle && <span className="text-[var(--cream-soft)]">OnlyFans: <span className="text-[var(--cream)]">{v.onlyfans_handle}</span></span>}
            {v.instagram_handle && <span className="text-[var(--cream-soft)]">Instagram: <span className="text-[var(--cream)]">{v.instagram_handle}</span></span>}
            {v.snapchat_handle && <span className="text-[var(--cream-soft)]">SnapChat: <span className="text-[var(--cream)]">{v.snapchat_handle}</span></span>}
          </div>
        </div>
      )}

      {v.note && (
        <div className="border-t border-white/10 pt-4 mb-3">
          <div className="font-meta mb-2">Notat fra søker</div>
          <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed whitespace-pre-wrap">{v.note}</p>
        </div>
      )}

      {v.status === "rejected" && v.reject_reason && (
        <div className="border-t border-white/10 pt-4">
          <div className="font-meta text-red-400 mb-1">Avvist grunn</div>
          <p className="font-manrope text-sm text-red-400">{v.reject_reason}</p>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "Til behandling", cls: "bg-[var(--gold)]/15 text-[var(--gold)]" },
    approved: { label: "Godkjent", cls: "bg-emerald-500/15 text-emerald-400" },
    rejected: { label: "Avvist", cls: "bg-red-500/15 text-red-400" },
  };
  const m = map[status] || map.pending;
  return <span className={`font-meta text-[10px] px-2 py-0.5 ${m.cls}`}>{m.label}</span>;
}

function SendBankIDLinkBtn({ verif }) {
  const [busy, setBusy] = useState(false);
  const sent = verif.bankid_status === "link_sent" || verif.bankid_link_sent_at;
  const verified = verif.bankid_status === "verified";

  const send = async () => {
    if (!window.confirm(`Send BankID-link til @${verif.username} sin innboks?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/seller-verifications/${verif.id}/send-bankid-link`);
      toast.success(`BankID-link lagt i innboksen til @${verif.username}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (verified) {
    return (
      <span className="inline-flex items-center gap-1 font-meta text-[10px] px-2 py-1 bg-emerald-500/15 text-emerald-400" data-testid={`verif-bankid-done-${verif.id}`}>
        <BadgeCheck className="h-3 w-3" /> BankID OK
      </span>
    );
  }
  return (
    <button onClick={send} disabled={busy}
      className="btn-ghost flex items-center gap-1"
      data-testid={`verif-bankid-link-${verif.id}`}
      title={sent ? "Send ny link (forrige overskrives)" : "Send BankID-verifiseringslink"}>
      <LinkIcon className="h-3.5 w-3.5" />
      {busy ? "Sender…" : sent ? "Send link på nytt" : "Send BankID-link"}
    </button>
  );
}

function PrivatePhoto({ verifId, which, apiBase, label }) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let revokeUrl = null;
    fetch(`${apiBase}/api/admin/seller-verifications/${verifId}/photo/${which}`, {
      credentials: "include",
    })
      .then((r) => r.ok ? r.blob() : Promise.reject(r.status))
      .then((blob) => {
        revokeUrl = URL.createObjectURL(blob);
        setSrc(revokeUrl);
      })
      .catch(() => setError(true));
    return () => { if (revokeUrl) URL.revokeObjectURL(revokeUrl); };
  }, [verifId, which, apiBase]);

  return (
    <div>
      <div className="font-meta mb-2">{label}</div>
      <div className="aspect-[3/4] bg-[var(--noir-elev)] border border-white/10 flex items-center justify-center overflow-hidden">
        {error ? (
          <div className="text-center text-[var(--cream-faint)] text-sm p-4">Kunne ikke laste bildet</div>
        ) : !src ? (
          <div className="text-[var(--cream-faint)] text-sm">Laster…</div>
        ) : (
          <a href={src} target="_blank" rel="noreferrer" className="block w-full h-full">
            <img src={src} alt={label} className="w-full h-full object-contain" />
          </a>
        )}
      </div>
    </div>
  );
}

// ============================================================
// STATISTIKK-BAR
// ============================================================
function StatsBar({ stats }) {
  if (!stats) return null;
  const items = [
    { label: "Åpne rapporter", value: stats.open_reports, accent: stats.open_reports > 0 },
    { label: "Rapporter 24t",  value: stats.reports_24h },
    { label: "Suspenderte",    value: stats.suspended_users },
    { label: "Skjulte bilder", value: stats.hidden_images },
    { label: "Nye brukere 24t", value: stats.new_users_24h },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="mod-stats">
      {items.map((it) => (
        <div key={it.label} className="panel-card p-4">
          <div className="font-meta text-[10px] text-[var(--cream-soft)] mb-1">{it.label}</div>
          <div className={`font-fraunces italic text-3xl ${it.accent ? "text-[var(--magenta)]" : "text-[var(--cream)]"}`}>
            {it.value ?? 0}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// RAPPORT-KØ
// ============================================================
function ReportQueue() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/admin/reports?status=${filter}`)
      .then((r) => setRows(r.data.results || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <section data-testid="report-queue">
      <div className="flex gap-2 mb-5">
        {["open", "resolved", "all"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 font-meta text-xs ${
              filter === f ? "bg-[var(--magenta)] text-black" : "border border-white/10 text-[var(--cream-soft)]"
            }`}
            data-testid={`filter-${f}`}
          >
            {f === "open" ? "Åpne" : f === "resolved" ? "Behandlet" : "Alle"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="font-meta text-[var(--cream-soft)]">Laster…</div>
      ) : rows.length === 0 ? (
        <div className="panel-tight p-10 text-center">
          <Flag className="h-10 w-10 text-[var(--cream-faint)] mx-auto mb-3" />
          <p className="font-fraunces italic text-2xl text-[var(--cream)]">Ingen {filter === "open" ? "åpne" : ""} rapporter</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => <ReportRow key={r.id} report={r} onChange={load} />)}
        </div>
      )}
    </section>
  );
}

function ReportRow({ report, onChange }) {
  const TargetIcon = TARGET_ICONS[report.target_type] || Flag;
  const t = report.target || {};
  const isUserReport = report.target_type === "user";
  const [busy, setBusy] = useState(false);

  const moderate = async (action) => {
    setBusy(true);
    try {
      await api.post("/admin/moderate", {
        target_type: report.target_type,
        target_id: report.target_id,
        action,
        reason: report.reason,
      });
      await api.post(`/admin/reports/${report.id}/resolve`, {
        action: action === "hide" ? "hidden" : action === "delete" ? "deleted" : "noop",
        notes: `Auto-handling: ${action} via rapport`,
      });
      toast.success("Handling registrert");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const resolve = async (action = "noop") => {
    setBusy(true);
    try {
      await api.post(`/admin/reports/${report.id}/resolve`, { action, notes: "" });
      toast.success("Rapport behandlet");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <article className="panel-card p-5" data-testid={`report-${report.id}`}>
      <div className="flex items-start gap-4">
        <TargetIcon className="h-5 w-5 text-[var(--magenta)] shrink-0 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="font-meta text-[var(--magenta)]">{report.target_type.toUpperCase()}</span>
            <span className="font-meta text-[var(--cream-soft)]">·</span>
            <span className="font-manrope text-sm text-[var(--cream)]">{report.reason}</span>
            {report.same_target_open_count > 1 && (
              <span className="px-2 py-0.5 bg-[var(--magenta)]/15 text-[var(--magenta)] font-meta text-[9px]">
                {report.same_target_open_count} RAPPORTER
              </span>
            )}
            <span className="ml-auto font-meta text-[10px] text-[var(--cream-faint)]">{relTime(report.created_at)}</span>
          </div>

          {/* Target preview */}
          {isUserReport ? (
            <div className="border border-white/10 p-3 mb-2 flex items-center gap-3">
              <div className="w-10 h-10 bg-[var(--noir)] rounded-sm overflow-hidden flex items-center justify-center">
                {t.avatar_url ? <img src={t.avatar_url} alt="" className="w-full h-full object-cover" />
                  : <UserIcon className="h-5 w-5 text-[var(--cream-faint)]" />}
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/profile/${t.username}`} className="font-fraunces italic text-lg text-[var(--cream)] hover:text-[var(--magenta)]">
                  @{t.username || "?"}
                </Link>
                {t.bio && <div className="text-xs text-[var(--cream-soft)] truncate">{t.bio}</div>}
                {t.suspended_at && <div className="font-meta text-[var(--magenta)] mt-1">SUSPENDERT</div>}
              </div>
            </div>
          ) : (
            <div className="border border-white/10 p-3 mb-2">
              {t.thumbnail_path || t.url ? (
                <img src={t.thumbnail_path || t.url} alt="" className="max-h-32 object-contain" />
              ) : (
                <div className="font-manrope text-sm text-[var(--cream-soft)] line-clamp-3">
                  {t.title || t.caption || t.content || t.teaser || <span className="italic">(ikke funnet)</span>}
                </div>
              )}
              {(t.is_hidden || t.is_deleted) && (
                <div className="font-meta text-[var(--magenta)] mt-2">
                  {t.is_deleted ? "SLETTET" : "SKJULT"}
                </div>
              )}
            </div>
          )}

          {report.details && (
            <p className="font-manrope text-sm text-[var(--cream-soft)] mb-2 leading-relaxed">
              «{report.details}»
            </p>
          )}

          <div className="font-meta text-[10px] text-[var(--cream-faint)] mb-3">
            Rapportert av <Link to={`/profile/${report.reporter?.username}`} className="text-[var(--magenta)] hover:underline">@{report.reporter?.username || "?"}</Link>
          </div>

          {report.status === "open" && (
            <div className="flex gap-2 flex-wrap">
              {!isUserReport && (
                <>
                  <button onClick={() => moderate("hide")} disabled={busy}
                    className="btn-ghost inline-flex items-center gap-2" data-testid={`hide-${report.id}`}>
                    <EyeOff className="h-3 w-3" /> Skjul
                  </button>
                  <button onClick={() => moderate("delete")} disabled={busy}
                    className="btn-ghost inline-flex items-center gap-2 text-[var(--magenta)] border-[var(--magenta)]/40" data-testid={`delete-${report.id}`}>
                    <Trash2 className="h-3 w-3" /> Slett
                  </button>
                </>
              )}
              <button onClick={() => resolve("noop")} disabled={busy}
                className="btn-ghost inline-flex items-center gap-2" data-testid={`dismiss-${report.id}`}>
                Avvis
              </button>
              {isUserReport && t.username && (
                <a href={`/admin/moderasjon?suspend=${t.username}`} className="btn-ghost inline-flex items-center gap-2 text-[var(--magenta)] border-[var(--magenta)]/40">
                  <Ban className="h-3 w-3" /> Suspendér
                </a>
              )}
            </div>
          )}
          {report.status === "resolved" && report.resolution_action && (
            <div className="font-meta text-[var(--cream-faint)]">
              BEHANDLET → {report.resolution_action.toUpperCase()}
              {report.resolution_notes && ` · ${report.resolution_notes}`}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

// ============================================================
// SUSPENDER BRUKER
// ============================================================
function UserSuspendTool() {
  const [username, setUsername] = useState("");
  const [days, setDays] = useState(7);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Skjul-ban state (separat)
  const [sbUsername, setSbUsername] = useState("");
  const [sbDays, setSbDays] = useState(7);
  const [sbReason, setSbReason] = useState("");
  const [sbBusy, setSbBusy] = useState(false);

  useEffect(() => {
    const u = new URLSearchParams(window.location.search).get("suspend");
    if (u) setUsername(u);
    const sb = new URLSearchParams(window.location.search).get("shadowban");
    if (sb) setSbUsername(sb);
  }, []);

  const suspend = async () => {
    if (!username || !reason) return toast.error("Brukernavn og årsak må fylles ut");
    setBusy(true);
    try {
      const u = await api.get(`/users/${username}`);
      await api.post(`/admin/users/${u.data.user.id}/suspend`, { days: Number(days), reason });
      toast.success(`@${username} suspendert i ${days} dager`);
      setUsername(""); setReason(""); setDays(7);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const shadowban = async () => {
    if (!sbUsername || !sbReason) return toast.error("Brukernavn og årsak må fylles ut");
    setSbBusy(true);
    try {
      const u = await api.get(`/users/${sbUsername}`);
      await api.post(`/admin/users/${u.data.user.id}/shadowban`, { days: Number(sbDays), reason: sbReason });
      toast.success(`@${sbUsername} skjul-bannet i ${sbDays} dager`);
      setSbUsername(""); setSbReason(""); setSbDays(7);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSbBusy(false); }
  };

  const unshadowban = async () => {
    if (!sbUsername) return toast.error("Skriv inn brukernavn først");
    if (!window.confirm(`Oppheve skjul-ban på @${sbUsername}?`)) return;
    setSbBusy(true);
    try {
      const u = await api.get(`/users/${sbUsername}`);
      await api.post(`/admin/users/${u.data.user.id}/unshadowban`, {});
      toast.success(`Skjul-ban opphevet for @${sbUsername}`);
      setSbUsername("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSbBusy(false); }
  };

  return (
    <section data-testid="user-suspend-tool" className="max-w-xl space-y-10">
      <div>
        <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-5">Suspender bruker</h2>
        <p className="text-xs text-[var(--cream-soft)] mb-4">
          Blokkerer innlogging. Brukeren får feilmelding ved forsøk.
        </p>
        <div className="space-y-4">
          <div>
            <label className="font-meta block mb-2">Brukernavn</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)}
              placeholder="brukernavn" className="input-editorial w-full"
              data-testid="suspend-username" />
          </div>
          <div>
            <label className="font-meta block mb-2">Antall dager</label>
            <input type="number" min="1" max="3650" value={days}
              onChange={(e) => setDays(e.target.value)}
              className="input-editorial w-full" data-testid="suspend-days" />
          </div>
          <div>
            <label className="font-meta block mb-2">Årsak (vises i e-post til bruker når aktiv)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
              rows={3} maxLength={500} className="input-editorial w-full"
              placeholder="F.eks. gjentatte brudd på innholdsregler"
              data-testid="suspend-reason" />
          </div>
          <button onClick={suspend} disabled={busy} className="btn-magenta inline-flex items-center gap-2"
            data-testid="suspend-submit">
            <Ban className="h-4 w-4" /> {busy ? "Suspenderer…" : "Suspendér"}
          </button>
        </div>
      </div>

      {/* Shadow-ban */}
      <div className="border-t border-white/10 pt-8">
        <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">Skjul-ban bruker</h2>
        <p className="text-xs text-[var(--cream-soft)] mb-4">
          Brukeren kan logge inn og bruke siden som normalt, men innhold og profil er usynlig
          for alle andre. Brukeren ser et banner om at skjul-ban er aktivt.
        </p>
        <div className="space-y-4">
          <div>
            <label className="font-meta block mb-2">Brukernavn</label>
            <input value={sbUsername} onChange={(e) => setSbUsername(e.target.value)}
              placeholder="brukernavn" className="input-editorial w-full"
              data-testid="shadowban-username" />
          </div>
          <div>
            <label className="font-meta block mb-2">Antall dager</label>
            <input type="number" min="1" max="3650" value={sbDays}
              onChange={(e) => setSbDays(e.target.value)}
              className="input-editorial w-full" data-testid="shadowban-days" />
          </div>
          <div>
            <label className="font-meta block mb-2">Årsak (vises i banner til bruker)</label>
            <textarea value={sbReason} onChange={(e) => setSbReason(e.target.value)}
              rows={3} maxLength={500} className="input-editorial w-full"
              placeholder="F.eks. gjentatte brudd på moderator-instrukser"
              data-testid="shadowban-reason" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={shadowban} disabled={sbBusy}
              className="btn-magenta inline-flex items-center gap-2"
              data-testid="shadowban-submit">
              <EyeOff className="h-4 w-4" /> {sbBusy ? "Aktiverer…" : "Aktiver skjul-ban"}
            </button>
            <button onClick={unshadowban} disabled={sbBusy || !sbUsername}
              className="btn-ghost inline-flex items-center gap-2 border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10 disabled:opacity-40"
              data-testid="shadowban-remove">
              Oppheve skjul-ban
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// FORCE-VERIFISER E-POST (Iter 47-rescue)
// ============================================================
function ForceVerifyEmailTool() {
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);

  const forceVerify = async () => {
    if (!username.trim()) return toast.error("Skriv inn brukernavn");
    setBusy(true);
    try {
      const u = await api.get(`/users/${username.trim()}`);
      await api.post(`/admin/users/${u.data.user.id}/force-email-verified`);
      toast.success(`E-post markert som verifisert for @${username}`);
      setUsername("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="force-verify-email-tool" className="max-w-xl">
      <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">Force-verifiser e-post</h2>
      <p className="text-sm text-[var(--cream-soft)] mb-5 leading-relaxed">
        Markerer en brukers e-post som verifisert uten at de må klikke lenken.
        Brukes når brukeren rapporterer at lenken ikke fungerer eller de har mistet tilgang til e-posten.
      </p>
      <div className="space-y-4">
        <div>
          <label className="font-meta block mb-2">Brukernavn</label>
          <input value={username} onChange={(e) => setUsername(e.target.value)}
            placeholder="f.eks. Scandiidoll" className="input-editorial w-full"
            data-testid="force-verify-username-input" />
        </div>
        <button
          onClick={forceVerify}
          disabled={busy}
          className="btn-magenta"
          data-testid="force-verify-submit-btn"
        >
          {busy ? "Verifiserer…" : "Force-verifiser e-post"}
        </button>
      </div>
    </section>
  );
}


function AuditLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/audit?limit=200").then((r) => setRows(r.data.results || [])).catch(() => {});
  }, []);

  return (
    <section data-testid="audit-log">
      <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-5">Moderasjons-historikk</h2>
      {rows.length === 0 ? (
        <div className="font-fraunces italic text-[var(--cream-soft)]">Ingen handlinger ennå.</div>
      ) : (
        <ul className="divide-y divide-white/5 border-y border-white/10">
          {rows.map((r) => (
            <li key={r.id} className="py-3 grid grid-cols-[100px_120px_1fr_80px] gap-3 items-baseline">
              <span className="font-meta text-[var(--cream-faint)]">{relTime(r.created_at)}</span>
              <span className="font-meta text-[var(--magenta)] truncate">{r.action}</span>
              <span className="font-manrope text-sm text-[var(--cream)] truncate">
                {r.target_type} · {r.target_id?.slice(0, 8)}…
              </span>
              <span className="font-meta text-[var(--cream-soft)] truncate">@{r.actor_username}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


// ============================================================
// CSAM-RAPPORT-KØ
// ============================================================
function CSAMQueue() {
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(true);

  const load = () => {
    setLoadingRows(true);
    Promise.all([
      api.get("/admin/csam-stats").then((r) => setStats(r.data)).catch(() => setStats(null)),
      api.get(`/admin/csam-reports${statusFilter ? `?status=${statusFilter}` : ""}`).then((r) => setRows(r.data.results || [])).catch(() => setRows([])),
    ]).finally(() => setLoadingRows(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const updateStatus = async (id, status, extra = {}) => {
    try {
      await api.patch(`/admin/csam-reports/${id}`, { status, ...extra });
      toast.success(`Rapport oppdatert: ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <section data-testid="csam-queue">
      <div className="bg-[var(--magenta)]/10 border border-[var(--magenta)]/30 px-4 py-3 mb-6 flex items-start gap-3">
        <AlertTriangle className="h-4 w-4 text-[var(--magenta)] mt-0.5 shrink-0" />
        <div className="text-sm text-[var(--cream)] leading-relaxed">
          <strong>Rettslig håndtering:</strong> Alle bekreftede match SKAL rapporteres til
          <a href="https://report.cybertip.org/" target="_blank" rel="noreferrer" className="underline mx-1">NCMEC CyberTipline</a>
          og Kripos (tips@politiet.no) snarest mulig. Brukerens konto er automatisk suspendert.
          Bevaring av bevis er lovpålagt – ikke slett rapport-dokumentet før etterforskning er avsluttet.
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="csam-stats">
          <div className="panel"><div className="panel-heading text-[var(--cream-faint)]">Backend</div><div className="font-fraunces italic text-xl text-[var(--cream)] capitalize">{stats.backend}</div></div>
          <div className="panel"><div className="panel-heading text-[var(--cream-faint)]">Skanninger 24t</div><div className="font-fraunces italic text-2xl text-[var(--cream)]">{stats.scans_last_24h}</div></div>
          <div className="panel"><div className="panel-heading text-[var(--cream-faint)]">Skanninger 7d</div><div className="font-fraunces italic text-2xl text-[var(--cream)]">{stats.scans_last_7d}</div></div>
          <div className="panel"><div className="panel-heading text-[var(--cream-faint)]">Venter på gjennomgang</div><div className="font-fraunces italic text-2xl text-[var(--magenta)]">{stats.pending_reports}</div></div>
          <div className="panel"><div className="panel-heading text-[var(--cream-faint)]">Skanner-feil 24t</div><div className={`font-fraunces italic text-2xl ${stats.scan_errors_24h > 0 ? "text-[var(--magenta)]" : "text-[var(--cream)]"}`}>{stats.scan_errors_24h}</div></div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {["pending_review", "reported_to_ncmec", "false_positive", "closed", ""].map((s) => (
          <button
            key={s || "alle"}
            onClick={() => setStatusFilter(s)}
            data-testid={`csam-filter-${s || "all"}`}
            className={`px-3 py-1.5 font-meta text-xs uppercase tracking-[0.12em] border ${
              statusFilter === s
                ? "border-[var(--magenta)] text-[var(--magenta)]"
                : "border-white/10 text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}
          >
            {s ? s.replace(/_/g, " ") : "Alle"}
          </button>
        ))}
      </div>

      {loadingRows ? (
        <div className="font-meta text-[var(--cream-soft)]">Laster rapporter…</div>
      ) : rows.length === 0 ? (
        <div className="font-meta text-[var(--cream-soft)]">Ingen rapporter i denne tilstanden.</div>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.id} className="panel" data-testid={`csam-row-${r.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldAlert className="h-4 w-4 text-[var(--magenta)] shrink-0" />
                    <span className="font-meta text-[10px] uppercase tracking-[0.15em] text-[var(--magenta)]">
                      {r.status?.replace(/_/g, " ")}
                    </span>
                    <span className="font-meta text-[10px] text-[var(--cream-faint)]">
                      · {relTime(r.created_at)} · backend: {r.backend}
                    </span>
                  </div>
                  <div className="text-sm text-[var(--cream)]">
                    Bruker:{" "}
                    {r.owner?.username ? (
                      <Link to={`/profile/${r.owner.username}`} className="text-[var(--magenta)] hover:underline">
                        @{r.owner.username}
                      </Link>
                    ) : (
                      <span className="text-[var(--cream-soft)]">ukjent</span>
                    )}
                    {r.owner?.is_suspended && (
                      <span className="ml-2 font-meta text-[10px] text-[var(--magenta)] uppercase tracking-[0.12em]">
                        SUSPENDERT
                      </span>
                    )}
                  </div>
                  <div className="font-meta text-xs text-[var(--cream-soft)] mt-1">
                    {r.media_type} · {r.media_id?.slice(0, 12)}… · sha {r.sha256?.slice(0, 12)}…
                  </div>
                </div>
              </div>

              {r.admin_notes && (
                <div className="bg-[var(--noir)] border border-white/10 p-2 mb-3 text-xs text-[var(--cream-soft)] whitespace-pre-wrap">
                  {r.admin_notes}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {r.status !== "reported_to_ncmec" && (
                  <button
                    onClick={() => {
                      const id = prompt("NCMEC CyberTipline report-ID (lim inn ID-en du fikk):");
                      if (id) updateStatus(r.id, "reported_to_ncmec", { ncmec_report_id: id });
                    }}
                    className="px-3 py-1.5 text-xs font-meta uppercase tracking-[0.12em] border border-[var(--magenta)] text-[var(--magenta)] hover:bg-[var(--magenta)] hover:text-black transition"
                    data-testid={`csam-mark-ncmec-${r.id}`}
                  >
                    Marker rapportert til NCMEC
                  </button>
                )}
                {r.status !== "closed" && r.status !== "false_positive" && (
                  <button
                    onClick={() => {
                      if (confirm("Bekreft: marker som false positive? Brukerens suspensjon oppheves.")) {
                        updateStatus(r.id, "false_positive");
                      }
                    }}
                    className="px-3 py-1.5 text-xs font-meta uppercase tracking-[0.12em] border border-white/15 text-[var(--cream)] hover:bg-white/5 transition"
                    data-testid={`csam-mark-fp-${r.id}`}
                  >
                    False positive
                  </button>
                )}
                {r.status !== "closed" && (
                  <button
                    onClick={() => updateStatus(r.id, "closed")}
                    className="px-3 py-1.5 text-xs font-meta uppercase tracking-[0.12em] border border-white/15 text-[var(--cream)] hover:bg-white/5 transition"
                    data-testid={`csam-close-${r.id}`}
                  >
                    Lukk sak
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


// ============================================================
// COACHING-FORESPØRSLER (OnlyFans-hjelp)
// ============================================================

const METHOD_LABEL = {
  email: "E-post", snapchat: "Snapchat", telegram: "Telegram", x: "X (Twitter)",
  norsk_erotikk_dm: "DM på Norsk Erotikk",
};

function CoachingRow({ r, busy, onMarkReplied, onClose, onAfterReply }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  // E-posten ligger i contact_handle når contact_method === "email"
  const inquiryEmail = r.email || (r.contact_method === "email" ? r.contact_handle : null);
  const isEmail = !!inquiryEmail && inquiryEmail.includes("@");
  const submitReply = async () => {
    if (!replyText.trim() || replyText.trim().length < 2) {
      toast.error("Skriv et svar først.");
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/admin/coaching-inquiries/${r.id}/reply`, { message: replyText.trim() });
      if (res.data?.success) {
        toast.success("Svar sendt på e-post.");
      } else {
        toast.warning("Lagret, men e-post kunne ikke sendes.");
      }
      setReplyText("");
      setReplyOpen(false);
      onAfterReply();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };
  return (
    <li className="panel-tight p-5" data-testid={`coaching-row-${r.id}`}>
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className="font-meta text-[var(--cream-soft)] text-xs">
            {relTime(r.created_at)} · via {METHOD_LABEL[r.contact_method] || r.contact_method}
            {r.username && <> · innlogget som <strong className="text-[var(--cream)]">{r.username}</strong></>}
          </div>
          <div className="font-fraunces text-xl text-[var(--cream)] mt-1">
            {r.contact_handle}
          </div>
        </div>
        <span className={`font-meta text-[10px] px-2 py-1 ${
          r.status === "new" ? "bg-[var(--magenta)] text-black" :
          r.status === "replied" ? "bg-green-700/40 text-green-200" :
          "bg-white/10 text-[var(--cream-soft)]"
        }`}>
          {r.status === "new" ? "NY" : r.status === "replied" ? "BESVART" : "LUKKET"}
        </span>
      </div>

      {r.has_existing_account && (
        <div className="mb-3 inline-block font-meta text-[10px] px-2 py-1 bg-amber-700/30 text-amber-200 border border-amber-700/50">
          Har allerede OnlyFans-konto
        </div>
      )}

      {r.existing_of_links && (
        <div className="mb-3">
          <div className="font-meta text-[10px] text-[var(--cream-faint)] mb-1">OF-PROFIL(ER)</div>
          <div className="font-manrope text-sm text-[var(--cream)] break-all">{r.existing_of_links}</div>
        </div>
      )}

      {r.message && (
        <div className="mt-3 pl-4 border-l-2 border-white/15 text-[var(--cream)] font-manrope text-sm whitespace-pre-wrap leading-relaxed">
          {r.message}
        </div>
      )}

      {/* Tidligere svar fra admin */}
      {Array.isArray(r.admin_replies) && r.admin_replies.length > 0 && (
        <div className="mt-4 space-y-2" data-testid={`coaching-replies-${r.id}`}>
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)]">SVAR SENDT</div>
          {r.admin_replies.map((rep, i) => (
            <div key={i} className="pl-4 border-l-2 border-[var(--magenta)]/50 text-sm text-[var(--cream-soft)]">
              <div className="font-meta text-[10px] text-[var(--cream-faint)] mb-1">
                {relTime(rep.sent_at)} · {rep.sent_by_username || "admin"}
                {!rep.success && <span className="text-red-300 ml-2">(ikke sendt på mail)</span>}
              </div>
              <div className="whitespace-pre-wrap">{rep.message}</div>
            </div>
          ))}
        </div>
      )}

      {/* Reply-form */}
      {replyOpen && (
        <div className="mt-4 panel p-4 border border-[var(--magenta)]/30" data-testid={`coaching-reply-form-${r.id}`}>
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-2">
            SVAR SOM E-POST TIL {inquiryEmail}
          </div>
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Skriv svaret ditt her. Avsender blir coaching@norskerotikk.no – brukerens svar tilbake havner i din admin-mail."
            rows={5}
            maxLength={4000}
            className="input-editorial w-full font-manrope text-sm"
            data-testid={`coaching-reply-text-${r.id}`}
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => { setReplyOpen(false); setReplyText(""); }}
              className="px-3 py-1.5 font-meta text-xs border border-white/20 text-[var(--cream-soft)]"
              data-testid={`coaching-reply-cancel-${r.id}`}
            >
              Avbryt
            </button>
            <button
              onClick={submitReply}
              disabled={sending}
              className="px-4 py-1.5 font-meta text-xs bg-[var(--magenta)] text-black disabled:opacity-50"
              data-testid={`coaching-reply-send-${r.id}`}
            >
              {sending ? "Sender…" : "SEND SVAR"}
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 flex gap-3 flex-wrap">
        {isEmail && !replyOpen && r.status !== "closed" && (
          <button
            onClick={() => setReplyOpen(true)}
            className="px-4 py-2 bg-[var(--magenta)] text-black font-meta text-xs"
            data-testid={`coaching-reply-${r.id}`}
          >
            SVAR PÅ E-POST
          </button>
        )}
        {r.contact_method === "norsk_erotikk_dm" && r.contact_handle && (
          <Link to={`/meldinger?til=${encodeURIComponent(r.contact_handle)}`}
            className="px-4 py-2 border border-[var(--magenta)] text-[var(--magenta)] font-meta text-xs hover:bg-[var(--magenta)] hover:text-black"
            data-testid={`coaching-open-dm-${r.id}`}>
            ÅPNE DM →
          </Link>
        )}
        {r.status === "new" && (
          <button onClick={() => onMarkReplied(r.id)} disabled={busy === r.id}
            className="px-4 py-2 border border-white/20 text-[var(--cream-soft)] font-meta text-xs hover:border-[var(--cream)] disabled:opacity-50"
            data-testid={`coaching-mark-replied-${r.id}`}>
            {busy === r.id ? "Lagrer…" : "Marker som besvart"}
          </button>
        )}
        {r.status !== "closed" && (
          <button onClick={() => onClose(r.id)} disabled={busy === r.id}
            className="px-4 py-2 border border-white/20 text-[var(--cream-soft)] font-meta text-xs hover:border-[var(--cream)] disabled:opacity-50"
            data-testid={`coaching-close-${r.id}`}>
            Lukk
          </button>
        )}
      </div>
    </li>
  );
}

function CoachingInquiryQueue() {
  const [status, setStatus] = useState("new");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/coaching-inquiries?status=${status}`);
      setRows(r.data?.results || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const markReplied = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/coaching-inquiries/${id}/mark-replied`);
      toast.success("Markert som besvart.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const closeIt = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/coaching-inquiries/${id}/close`);
      toast.success("Lukket.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const onAfterReply = () => { load(); };

  return (
    <section data-testid="coaching-queue">
      <div className="flex items-baseline justify-between mb-6 gap-4 flex-wrap">
        <h2 className="font-fraunces italic text-3xl text-[var(--cream)]">Coaching-forespørsler</h2>
        <div className="flex gap-2">
          {[
            { k: "new", label: "Nye" },
            { k: "replied", label: "Besvart" },
            { k: "closed", label: "Lukket" },
            { k: "all", label: "Alle" },
          ].map((f) => (
            <button key={f.k}
              onClick={() => setStatus(f.k)}
              data-testid={`coaching-filter-${f.k}`}
              className={`px-3 py-1.5 font-meta text-xs ${
                status === f.k
                  ? "bg-[var(--magenta)] text-black"
                  : "border border-white/15 text-[var(--cream-soft)] hover:border-[var(--cream)]"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-[var(--cream-soft)] py-12 text-center">Laster…</div>
      ) : rows.length === 0 ? (
        <div className="panel-tight p-12 text-center text-[var(--cream-soft)]">
          Ingen forespørsler i denne kategorien.
        </div>
      ) : (
        <ul className="space-y-4">
          {rows.map((r) => (
            <CoachingRow
              key={r.id}
              r={r}
              busy={busy}
              onMarkReplied={markReplied}
              onClose={closeIt}
              onAfterReply={onAfterReply}
            />
          ))}
        </ul>
      )}
    </section>
  );
}


// ============================================================
//  FeedbackQueue – brukernes tilbakemeldinger fra beta-banneret
// ============================================================
function FeedbackQueue() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("new");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = status === "all" ? "" : `?status=${status}`;
      const r = await api.get(`/admin/feedback${q}`);
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/feedback/${id}`, { status: newStatus });
      toast.success("Oppdatert");
      load();
    } catch {
      toast.error("Kunne ikke oppdatere");
    }
  };

  const kindLabel = (k) => ({ bug: "Feil", wish: "Ønske", praise: "Ros", other: "Annet" }[k] || k);
  const kindColor = (k) => ({ bug: "text-red-300", wish: "text-blue-300", praise: "text-emerald-300", other: "text-amber-300" }[k] || "text-[var(--cream)]");

  return (
    <section data-testid="feedback-queue">
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="font-fraunces italic text-3xl text-[var(--cream)]">Tilbakemeldinger</h2>
        <span className="font-meta text-[10px] text-[var(--cream-faint)]">{counts.total ?? 0} totalt</span>
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {[
          { k: "new", label: "Nye" },
          { k: "in_progress", label: "Pågår" },
          { k: "resolved", label: "Løst" },
          { k: "closed", label: "Lukket" },
          { k: "all", label: "Alle" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setStatus(f.k)}
            className={`px-3 py-1.5 text-xs border ${status === f.k ? "bg-[var(--magenta)] text-[var(--noir)] border-[var(--magenta)]" : "border-white/15 text-[var(--cream-soft)] hover:border-white/30"}`}
            data-testid={`feedback-filter-${f.k}`}
          >
            {f.label}
            {f.k !== "all" && counts[f.k] != null && <span className="ml-1.5 opacity-70">({counts[f.k]})</span>}
          </button>
        ))}
      </div>

      {loading && <div className="text-[var(--cream-soft)] text-sm">Laster …</div>}

      {!loading && items.length === 0 && (
        <div className="panel-tight p-8 text-center text-[var(--cream-soft)] font-fraunces italic">
          Ingen tilbakemeldinger i denne kategorien.
        </div>
      )}

      {!loading && items.length > 0 && (
        <ul className="space-y-3">
          {items.map((r) => (
            <li key={r.id} className="panel-tight p-5" data-testid={`feedback-row-${r.id}`}>
              <div className="flex items-baseline justify-between mb-2 gap-3">
                <div className="flex items-baseline gap-3 min-w-0">
                  <span className={`font-meta text-[10px] tracking-[0.2em] ${kindColor(r.kind)}`}>{kindLabel(r.kind).toUpperCase()}</span>
                  <span className="font-fraunces text-[var(--cream)] truncate">
                    {r.username ? `@${r.username}` : "Anonym"}
                  </span>
                </div>
                <span className="font-meta text-[10px] text-[var(--cream-faint)] shrink-0">
                  {new Date(r.created_at).toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" })}
                </span>
              </div>
              <p className="text-[var(--cream-soft)] whitespace-pre-wrap text-sm leading-relaxed mb-3">{r.message}</p>
              <div className="font-meta text-[10px] text-[var(--cream-faint)] space-y-0.5 mb-3">
                {r.page_url && <div>Side: <span className="text-[var(--cream-soft)]">{r.page_url}</span></div>}
                {r.contact && <div>Kontakt: <span className="text-[var(--cream-soft)]">{r.contact}</span></div>}
                {r.user_agent && <div className="truncate">UA: <span className="text-[var(--cream-soft)]">{r.user_agent}</span></div>}
              </div>
              <div className="flex gap-2 flex-wrap">
                {r.status !== "in_progress" && (
                  <button onClick={() => updateStatus(r.id, "in_progress")} className="px-3 py-1.5 text-xs border border-white/15 text-[var(--cream-soft)] hover:border-white/30">Sett som «pågår»</button>
                )}
                {r.status !== "resolved" && (
                  <button onClick={() => updateStatus(r.id, "resolved")} className="px-3 py-1.5 text-xs bg-emerald-600/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-600/30">Marker som løst</button>
                )}
                {r.status !== "closed" && (
                  <button onClick={() => updateStatus(r.id, "closed")} className="px-3 py-1.5 text-xs border border-white/15 text-[var(--cream-faint)] hover:border-white/30">Lukk</button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ============================================================
//  PromoCodeAdmin – generer og administrer gratis-VIP-koder
// ============================================================
function PromoCodeAdmin() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("active");
  const [loading, setLoading] = useState(false);

  // generator-skjema
  const [kind, setKind] = useState("vip");
  const [period, setPeriod] = useState("90d");
  const [count, setCount] = useState(1);
  const [note, setNote] = useState("");
  const [customCode, setCustomCode] = useState("");
  const [useCustomDate, setUseCustomDate] = useState(false);
  const [customDate, setCustomDate] = useState(""); // YYYY-MM-DD
  const [generating, setGenerating] = useState(false);
  const [lastGenerated, setLastGenerated] = useState([]);
  // Avansert / affiliate-delbar kode
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [maxUses, setMaxUses] = useState(""); // tom = 1 (engangs)
  const [codeValidDate, setCodeValidDate] = useState(""); // separat utløp for selve koden
  const [affiliateUsername, setAffiliateUsername] = useState("");
  const [affiliateChips, setAffiliateChips] = useState("");
  const [affiliateRequireVerified, setAffiliateRequireVerified] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const r = await api.get(`/admin/promo-codes${q}`);
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const generate = async () => {
    setGenerating(true);
    setLastGenerated([]);
    try {
      const hasCustomCode = !!customCode.trim();
      // Med custom kode: ANTALL betyr "max bruk" (én kode, N innløsninger)
      // Uten custom kode: ANTALL betyr "antall unike koder" (N koder, hver til 1 person)
      const countNum = Math.max(1, Number(count) || 1);
      const body = {
        kind,
        period,
        count: hasCustomCode ? 1 : countNum,
        note,
      };
      if (hasCustomCode) {
        body.custom_code = customCode.trim().toUpperCase();
        if (countNum > 1) body.max_uses = countNum;
      }
      if (useCustomDate && customDate) {
        // Bruk slutten av valgt dag (23:59:59) i lokal tid, send som ISO
        const d = new Date(customDate + "T23:59:59");
        body.expires_until = d.toISOString();
      }
      // Avanserte felter — kan overstyre max_uses fra custom-kode-feltet, og legger til affiliate
      if (showAdvanced) {
        if (maxUses && Number(maxUses) > 1) {
          body.max_uses = Number(maxUses);
        }
        if (codeValidDate) {
          const d = new Date(codeValidDate + "T23:59:59");
          body.code_valid_until = d.toISOString();
        }
        if (affiliateUsername.trim()) {
          body.affiliate_username = affiliateUsername.trim();
          const chips = Number(affiliateChips);
          if (!chips || chips < 1) {
            toast.error("Affiliate krever chips per signup (≥1)");
            setGenerating(false);
            return;
          }
          body.affiliate_reward_chips = chips;
          body.affiliate_require_verified = affiliateRequireVerified;
        }
      }
      const r = await api.post("/admin/promo-codes", body);
      setLastGenerated(r.data.codes || []);
      toast.success(`${r.data.codes.length} kode${r.data.codes.length === 1 ? "" : "r"} generert`);
      setNote("");
      setCustomCode("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke generere");
    } finally {
      setGenerating(false);
    }
  };

  const remove = async (id, code) => {
    const c = items.find((x) => x.id === id);
    const isMulti = (c?.max_uses || 1) > 1;
    const hasAffiliate = !!c?.affiliate_username;
    const redeemed = c?.redeemed_count || 0;
    const isSensitive = isMulti || hasAffiliate || redeemed > 0;

    // For affiliate/multi-use/innløste koder: ekstra streng bekreftelse
    if (isSensitive) {
      const lines = [
        `Du er i ferd med å slette en aktiv kode: ${code}`,
        "",
        isMulti ? `• Delbar kode med ${redeemed}/${c.max_uses} innløsninger` : null,
        hasAffiliate ? `• Affiliate: @${c.affiliate_username} (${c.affiliate_reward_chips} chips/signup)` : null,
        redeemed > 0 ? `• ${redeemed} bruker(e) har allerede løst inn denne` : null,
        "",
        "Hvis du har delt denne koden ut (sosiale medier, OF, e-post osv.),",
        "vil mottakerne ikke kunne bruke koden lenger.",
        "",
        "Skriv inn KODEN nedenfor for å bekrefte:",
      ].filter(Boolean).join("\n");
      const typed = window.prompt(lines, "");
      if (!typed || typed.trim().toUpperCase() !== code.toUpperCase()) {
        toast.info("Avbrutt");
        return;
      }
    } else {
      if (!window.confirm(`Slette koden ${code}?`)) return;
    }
    try {
      await api.delete(`/admin/promo-codes/${id}`);
      toast.success("Koden er flyttet til Slettede. Kan gjenopprettes derfra.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette");
    }
  };

  const restore = async (id, code) => {
    try {
      await api.post(`/admin/promo-codes/${id}/restore`);
      toast.success(`${code} er gjenopprettet`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gjenopprette");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Kode ${code} kopiert`);
  };

  const PERIODS = [
    { v: "7d", l: "7 dager", vipOnly: true },
    { v: "30d", l: "30 dager" },
    { v: "90d", l: "90 dager" },
    { v: "365d", l: "365 dager" },
  ];
  const visiblePeriods = PERIODS.filter((p) => kind === "vip" || !p.vipOnly);

  // Hvis bruker bytter til creator_vip eller vip_creator_combo mens 7d er valgt, hopp til 30d
  useEffect(() => {
    if ((kind === "creator_vip" || kind === "vip_creator_combo") && period === "7d") setPeriod("30d");
    // eslint-disable-next-line
  }, [kind]);

  const kindLabel = (k) => {
    if (k === "vip_creator_combo") return "VIP+CREATOR";
    if (k === "creator_vip") return "CREATOR";
    return "VIP";
  };

  return (
    <section data-testid="promo-code-admin">
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="font-fraunces italic text-3xl text-[var(--cream)]">Promo-koder</h2>
        <span className="font-meta text-[10px] text-[var(--cream-faint)]">
          {counts.active ?? 0} aktive · {counts.used ?? 0} brukt · {counts.total ?? 0} totalt
        </span>
      </div>

      <div className="panel-tight p-5 mb-6 space-y-4" data-testid="promo-generator">
        <h3 className="font-fraunces italic text-xl text-[var(--cream)]">Generer nye koder</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">TYPE</label>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setKind("vip")} data-testid="promo-kind-vip" className={`px-3 py-1.5 text-xs border ${kind === "vip" ? "bg-[var(--magenta)] text-[var(--noir)] border-[var(--magenta)]" : "border-white/15 text-[var(--cream-soft)]"}`}>VIP</button>
              <button onClick={() => setKind("creator_vip")} data-testid="promo-kind-creator" className={`px-3 py-1.5 text-xs border ${kind === "creator_vip" ? "bg-[var(--magenta)] text-[var(--noir)] border-[var(--magenta)]" : "border-white/15 text-[var(--cream-soft)]"}`}>Creator</button>
              <button onClick={() => setKind("vip_creator_combo")} data-testid="promo-kind-combo" className={`px-3 py-1.5 text-xs border ${kind === "vip_creator_combo" ? "bg-[var(--magenta)] text-[var(--noir)] border-[var(--magenta)]" : "border-white/15 text-[var(--cream-soft)]"}`}>VIP + Creator</button>
            </div>
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-1.5">
              {kind === "vip" && "Gir vanlig VIP-status."}
              {kind === "creator_vip" && "Gir creator-tilgang (skapersiden synlig). Auto-godkjenner bruker som skaper hvis ikke allerede."}
              {kind === "vip_creator_combo" && "Gir både VIP og creator-tilgang."}
            </div>
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              PERIODE {useCustomDate && <span className="text-[var(--cream-faint)] normal-case tracking-normal">(ignoreres – egendefinert dato brukes)</span>}
            </label>
            <div className={`flex gap-2 flex-wrap ${useCustomDate ? "opacity-40 pointer-events-none" : ""}`}>
              {visiblePeriods.map((p) => (
                <button key={p.v} onClick={() => setPeriod(p.v)} className={`px-3 py-1.5 text-xs border ${period === p.v ? "bg-[var(--magenta)] text-[var(--noir)] border-[var(--magenta)]" : "border-white/15 text-[var(--cream-soft)]"}`}>{p.l}</button>
              ))}
            </div>

            <label className="flex items-center gap-2 mt-3 cursor-pointer">
              <input type="checkbox" checked={useCustomDate} onChange={(e) => setUseCustomDate(e.target.checked)} data-testid="promo-use-custom-date" />
              <span className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-soft)]">EGENDEFINERT SLUTTDATO</span>
            </label>
            {useCustomDate && (
              <input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                className="input-editorial mt-2 w-48"
                data-testid="promo-custom-date"
              />
            )}
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {customCode.trim() ? "ANTALL BRUK" : "ANTALL"}
            </label>
            <input
              type="number"
              min={1}
              max={customCode.trim() ? 10000 : 100}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              className="input-editorial w-32"
              data-testid="promo-count"
            />
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-1">
              {customCode.trim()
                ? "Hvor mange ganger denne ene koden kan løses inn."
                : "Antall unike koder som genereres (hver til 1 person)."}
            </div>
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">EGENDEFINERT KODE (valgfritt)</label>
            <input
              value={customCode}
              onChange={(e) => setCustomCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))}
              placeholder="f.eks. SNAKIE2026"
              maxLength={32}
              className="input-editorial w-full font-mono tracking-wider"
              data-testid="promo-custom-code"
            />
            <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-1">A-Z, 0-9, - og _. La stå tom for å auto-generere.</div>
          </div>

          <div className="md:col-span-2">
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">NOTAT (valgfritt)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="f.eks. 'Snakiebaby – OF-avtale 2026'" className="input-editorial w-full" data-testid="promo-note" />
          </div>
        </div>

        {/* AVANSERT — delbar/affiliate-kode */}
        <div className="border-t border-white/10 pt-4 mt-2">
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] hover:text-[var(--cream)]"
            data-testid="promo-advanced-toggle"
          >
            {showAdvanced ? "▾ SKJUL AVANSERT" : "▸ AVANSERT — DELBAR KODE / AFFILIATE"}
          </button>

          {showAdvanced && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4" data-testid="promo-advanced-section">
              <div>
                <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">MAX BRUK PER KODE</label>
                <input
                  type="number"
                  min={1}
                  max={10000}
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value)}
                  placeholder="1 (engangs)"
                  className="input-editorial w-32"
                  data-testid="promo-max-uses"
                />
                <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-1">
                  La stå tom for engangs. Sett &gt;1 for delbar kode (samme kode kan brukes av flere ulike personer).
                </div>
              </div>

              <div>
                <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
                  KODE-VARIGHET (KODEN SLUTTER Å VIRKE)
                </label>
                <input
                  type="date"
                  value={codeValidDate}
                  onChange={(e) => setCodeValidDate(e.target.value)}
                  min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                  className="input-editorial w-48"
                  data-testid="promo-code-valid-until"
                />
                <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-1">
                  Separat fra VIP-perioden. VIP varer fortsatt {period} fra innløsning, men ingen kan løse inn etter denne datoen.
                </div>
              </div>

              <div>
                <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">AFFILIATE — BRUKERNAVN (valgfritt)</label>
                <input
                  value={affiliateUsername}
                  onChange={(e) => setAffiliateUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
                  placeholder="f.eks. snakiebaby"
                  maxLength={32}
                  className="input-editorial w-full"
                  data-testid="promo-affiliate-username"
                />
                <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-1">
                  Denne brukeren får chips hver gang noen løser inn koden.
                </div>
              </div>

              <div>
                <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">CHIPS PER INNLØSNING</label>
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={affiliateChips}
                  onChange={(e) => setAffiliateChips(e.target.value)}
                  placeholder="f.eks. 4"
                  className="input-editorial w-32"
                  data-testid="promo-affiliate-chips"
                  disabled={!affiliateUsername.trim()}
                />
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={affiliateRequireVerified}
                    onChange={(e) => setAffiliateRequireVerified(e.target.checked)}
                    disabled={!affiliateUsername.trim()}
                    data-testid="promo-affiliate-require-verified"
                  />
                  <span className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-soft)]">
                    KREV E-POST-VERIFISERING FØR CHIPS UTBETALES
                  </span>
                </label>
                <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-1 ml-6">
                  Anbefalt på. Forhindrer chips for falske/spam-signup. Chips utbetales automatisk når innløseren bekrefter e-posten.
                </div>
              </div>
            </div>
          )}
        </div>

        <button onClick={generate} disabled={generating} className="btn-magenta disabled:opacity-40" data-testid="promo-generate">
          {generating ? "GENERERER…" : "GENERER KODER"}
        </button>

        {lastGenerated.length > 0 && (
          <div className="mt-3 p-3 border border-[var(--magenta)]/40 bg-[var(--magenta)]/10">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-2">NYE KODER ({lastGenerated.length})</div>
            <ul className="space-y-1">
              {lastGenerated.map((c) => (
                <li key={c.id} className="flex items-center justify-between">
                  <span className="font-mono text-[var(--cream)] tracking-wider">{c.code}</span>
                  <button onClick={() => copyCode(c.code)} className="font-meta text-[10px] text-[var(--magenta)] hover:text-[var(--cream)]">KOPIER</button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {[
          { k: "active", l: "Aktive" },
          { k: "used", l: "Brukte" },
          { k: "all", l: "Alle" },
          { k: "deleted", l: `Slettede${counts.deleted ? ` (${counts.deleted})` : ""}` },
        ].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} className={`px-3 py-1.5 text-xs border ${filter === f.k ? "bg-[var(--magenta)] text-[var(--noir)] border-[var(--magenta)]" : "border-white/15 text-[var(--cream-soft)] hover:border-white/30"}`} data-testid={`promo-filter-${f.k}`}>{f.l}</button>
        ))}
      </div>

      {loading && <div className="text-[var(--cream-soft)] text-sm">Laster …</div>}

      {!loading && items.length === 0 && (
        <div className="panel-tight p-8 text-center text-[var(--cream-soft)] font-fraunces italic">Ingen koder her.</div>
      )}

      {!loading && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((c) => {
            const isMulti = (c.max_uses || 1) > 1;
            const redeemedCount = c.redeemed_count || 0;
            const codeValid = c.code_valid_until ? new Date(c.code_valid_until) : null;
            const codeExpired = codeValid && codeValid <= new Date();
            const fixedExp = c.expires_until ? new Date(c.expires_until) : null;
            const fixedExpired = fixedExp && fixedExp <= new Date();
            // Multi-use koder anses som "brukt opp" kun når ALLE slots er fylt,
            // IKKE basert på `used`-flagget (som kan være feil-satt av legacy-kode).
            const codeExhausted = isMulti
              ? redeemedCount >= (c.max_uses || 1)
              : (c.used || redeemedCount >= 1);
            const expired = codeExpired || fixedExpired;
            const isDead = codeExhausted || expired;
            return (
              <li key={c.id} className="panel-tight p-4 flex items-center gap-4 flex-wrap" data-testid={`promo-row-${c.id}`}>
                <span className={`font-mono tracking-wider ${isDead ? "text-[var(--cream-faint)] line-through" : "text-[var(--cream)]"}`}>{c.code}</span>
                <span className="font-meta text-[10px] text-[var(--cream-faint)]">
                  {kindLabel(c.kind)} · {c.days}d
                  {c.expires_until && ` · låst til ${new Date(c.expires_until).toLocaleDateString("nb-NO")}`}
                  {codeValid && ` · kode-utløp ${codeValid.toLocaleDateString("nb-NO")}`}
                </span>
                {isMulti && !isDead && (
                  <span className="font-meta text-[10px] text-[var(--magenta)]">
                    DELBAR
                  </span>
                )}
                {c.affiliate_username && (
                  <span className="font-meta text-[10px] text-[var(--gold)]" title={`Total utbetalt: ${c.affiliate_chips_paid_total || 0} chips`}>
                    @{c.affiliate_username} · {c.affiliate_reward_chips} chips/signup · {c.affiliate_chips_paid_total || 0} utbetalt
                  </span>
                )}
                {c.note && <span className="text-xs text-[var(--cream-soft)] truncate max-w-[260px]">{c.note}</span>}
                <span className="ml-auto font-meta text-[10px] text-[var(--cream-faint)]">
                  {expired ? "Utløpt" :
                   codeExhausted && isMulti ? `Brukt opp (${redeemedCount}/${c.max_uses})` :
                   codeExhausted ? `Brukt av @${c.used_by_username || "?"}` :
                   isMulti ? `Aktiv (${redeemedCount}/${c.max_uses} brukt)` : "Aktiv"}
                </span>
                {!isDead && !c.deleted_at && (
                  <>
                    <button onClick={() => copyCode(c.code)} className="font-meta text-[10px] text-[var(--magenta)] hover:text-[var(--cream)]" data-testid={`promo-copy-${c.id}`}>KOPIER</button>
                    <button onClick={() => remove(c.id, c.code)} className="font-meta text-[10px] text-red-300 hover:text-red-200" data-testid={`promo-delete-${c.id}`}>SLETT</button>
                  </>
                )}
                {c.deleted_at && (
                  <>
                    <span className="font-meta text-[10px] text-red-300">SLETTET {new Date(c.deleted_at).toLocaleDateString("nb-NO")}</span>
                    <button onClick={() => restore(c.id, c.code)} className="font-meta text-[10px] text-[var(--magenta)] hover:text-[var(--cream)]" data-testid={`promo-restore-${c.id}`}>GJENOPPRETT</button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}



// ============================================================
// SYSTEM-MELDINGER (admin)
// ============================================================
function SystemMessageAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState("info");
  const [audience, setAudience] = useState("all");
  const [useExpiry, setUseExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/system-messages");
      setItems(r.data?.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!title.trim() || !body.trim()) return;
    setCreating(true);
    try {
      const payload = { title, body, severity, audience };
      if (useExpiry && expiryDate) {
        payload.expires_at = new Date(expiryDate + "T23:59:59").toISOString();
      }
      await api.post("/admin/system-messages", payload);
      toast.success("System-melding publisert");
      setTitle(""); setBody(""); setSeverity("info"); setAudience("all");
      setUseExpiry(false); setExpiryDate("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke opprette");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Slett denne meldingen? Den forsvinner fra alle brukere.")) return;
    try {
      await api.delete(`/admin/system-messages/${id}`);
      toast.success("Slettet");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette");
    }
  };

  const audienceLabel = (a) =>
    a === "creators" ? "BARE SKAPERE" :
    a === "sellers"  ? "BARE SELGERE" : "ALLE INNLOGGEDE";

  return (
    <section data-testid="system-message-admin">
      <h2 className="font-fraunces italic text-3xl text-[var(--cream)] mb-2">System-meldinger</h2>
      <p className="text-sm text-[var(--cream-soft)] mb-6">
        For oppdaterings-varsler, planlagt nedetid og andre viktige beskjeder fra plattformen.
        Vises som banner (info) eller blokkerende modal (viktig) for valgt målgruppe.
      </p>

      <div className="panel-tight p-5 mb-6 space-y-4" data-testid="sysmsg-create-form">
        <h3 className="font-fraunces italic text-xl text-[var(--cream)]">Ny melding</h3>

        <div>
          <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">TITTEL</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
            placeholder="f.eks. Planlagt nedetid lørdag kl. 02-04"
            className="input-editorial w-full" data-testid="sysmsg-title" />
        </div>

        <div>
          <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">BESKJED</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} rows={4}
            placeholder="Hele teksten brukeren skal se. Linjeskift bevares."
            className="input-editorial w-full" data-testid="sysmsg-body" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">ALVORLIGHET</label>
            <div className="flex gap-2">
              <button onClick={() => setSeverity("info")}
                className={`px-3 py-1.5 text-xs border ${severity === "info" ? "bg-[var(--magenta)] text-[var(--noir)] border-[var(--magenta)]" : "border-white/15 text-[var(--cream-soft)]"}`}
                data-testid="sysmsg-sev-info">Info (banner)</button>
              <button onClick={() => setSeverity("important")}
                className={`px-3 py-1.5 text-xs border ${severity === "important" ? "bg-[var(--magenta)] text-[var(--noir)] border-[var(--magenta)]" : "border-white/15 text-[var(--cream-soft)]"}`}
                data-testid="sysmsg-sev-imp">Viktig (blokkerer)</button>
            </div>
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">MÅLGRUPPE</label>
            <div className="flex gap-2 flex-wrap">
              {[
                ["all", "Alle"],
                ["creators", "Skapere"],
                ["sellers", "Selgere"],
              ].map(([v, l]) => (
                <button key={v} onClick={() => setAudience(v)}
                  className={`px-3 py-1.5 text-xs border ${audience === v ? "bg-[var(--magenta)] text-[var(--noir)] border-[var(--magenta)]" : "border-white/15 text-[var(--cream-soft)]"}`}
                  data-testid={`sysmsg-aud-${v}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={useExpiry} onChange={(e) => setUseExpiry(e.target.checked)} data-testid="sysmsg-use-expiry" />
            <span className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-soft)]">VALGFRI UTLØPSDATO</span>
          </label>
          {useExpiry && (
            <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
              min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
              className="input-editorial mt-2 w-48" data-testid="sysmsg-expiry" />
          )}
        </div>

        <button onClick={create} disabled={creating || !title.trim() || !body.trim()}
          className="btn-magenta disabled:opacity-40" data-testid="sysmsg-publish">
          {creating ? "PUBLISERER…" : "PUBLISER MELDING"}
        </button>
      </div>

      <h3 className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-3">PUBLISERTE MELDINGER</h3>
      {loading && <div className="text-sm text-[var(--cream-soft)]">Laster…</div>}
      {!loading && items.length === 0 && (
        <div className="panel-tight p-6 text-center text-[var(--cream-soft)] font-fraunces italic">Ingen meldinger publisert.</div>
      )}
      {!loading && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((m) => (
            <li key={m.id} className="panel-tight p-4" data-testid={`sysmsg-row-${m.id}`}>
              <div className="flex items-baseline gap-3 mb-1.5">
                <span className={`font-meta text-[10px] tracking-[0.2em] ${m.severity === "important" ? "text-[var(--magenta)]" : "text-[var(--cream-faint)]"}`}>
                  {m.severity === "important" ? "VIKTIG" : "INFO"} · {audienceLabel(m.audience)}
                </span>
                {m.is_expired && <span className="font-meta text-[10px] text-red-300">UTLØPT</span>}
                <span className="ml-auto font-meta text-[10px] text-[var(--cream-faint)]">
                  {new Date(m.created_at).toLocaleString("nb-NO")}
                </span>
                <button onClick={() => remove(m.id)} className="font-meta text-[10px] text-red-300 hover:text-red-200">SLETT</button>
              </div>
              <div className="font-fraunces italic text-lg text-[var(--cream)]">{m.title}</div>
              <div className="text-sm text-[var(--cream-soft)] whitespace-pre-line mt-1">{m.body}</div>
              {m.expires_at && (
                <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-2">
                  Utløper {new Date(m.expires_at).toLocaleString("nb-NO")}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ============================================================
// IKKE VERIFISERT-E-POST – liste over alle med email_verified=false
// ============================================================
function UnverifiedUsersList() {
  const [data, setData] = useState({ results: [], verified: 0, unverified: 0, total_users: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [newEmail, setNewEmail] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/unverified-users?limit=300")
      .then((r) => setData(r.data))
      .catch(() => setData({ results: [], verified: 0, unverified: 0, total_users: 0 }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const resend = async (uid) => {
    setBusy(uid);
    try {
      await api.post(`/admin/unverified-users/${uid}/resend`);
      toast.success("Verifiseringsmail sendt");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const forceVerify = async (uid, email) => {
    if (!window.confirm(`Tving e-postverifisering for ${email}?`)) return;
    setBusy(uid);
    try {
      await api.post(`/admin/users/${uid}/force-email-verified`);
      toast.success("E-post tvunget verifisert");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const startEditEmail = (uid, currentEmail) => {
    setEditingId(uid);
    setNewEmail(currentEmail || "");
  };

  const cancelEditEmail = () => {
    setEditingId(null);
    setNewEmail("");
  };

  const saveEmail = async (uid) => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Ugyldig e-post");
      return;
    }
    setBusy(uid);
    try {
      await api.patch(`/admin/users/${uid}/email`, { email });
      toast.success("E-post oppdatert. Ny verifiseringsmail sendt.");
      setEditingId(null);
      setNewEmail("");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail;
      toast.error(msg || formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const deleteUser = async (uid, username) => {
    if (!window.confirm(`Slette kontoen «${username}»?\n\nBrukernavnet blir ledig for ny registrering. Kan ikke angres via UI.`)) return;
    setBusy(uid);
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success(`«${username}» slettet. Brukernavnet er nå ledig.`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail;
      toast.error(msg || formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const pct = data.total_users > 0 ? Math.round((data.unverified / data.total_users) * 100) : 0;

  return (
    <section data-testid="unverified-users-list">
      <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-4">Ikke verifisert</h2>
      <div className="panel-tight p-4 mb-6 flex flex-wrap gap-6">
        <div>
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)]">TOTALT</div>
          <div className="font-fraunces italic text-2xl text-[var(--cream)]">{data.total_users}</div>
        </div>
        <div>
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)]">VERIFISERT</div>
          <div className="font-fraunces italic text-2xl text-emerald-300">{data.verified}</div>
        </div>
        <div>
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)]">IKKE VERIFISERT</div>
          <div className="font-fraunces italic text-2xl text-amber-400">{data.unverified} <span className="text-sm text-[var(--cream-faint)]">({pct}%)</span></div>
        </div>
      </div>

      {loading && <div className="text-sm text-[var(--cream-soft)]">Laster…</div>}
      {!loading && data.results.length === 0 && (
        <div className="panel-tight p-6 text-center text-[var(--cream-soft)] font-fraunces italic">
          Ingen uverifiserte brukere. Alle har bekreftet e-posten sin.
        </div>
      )}
      {!loading && data.results.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] border-b border-white/10">
                <th className="text-left py-2 px-2">BRUKER</th>
                <th className="text-left py-2 px-2">E-POST</th>
                <th className="text-left py-2 px-2">OPPRETTET</th>
                <th className="text-left py-2 px-2">INNL.</th>
                <th className="text-left py-2 px-2">SISTE IP</th>
                <th className="text-left py-2 px-2">TOKEN</th>
                <th className="text-right py-2 px-2">HANDLINGER</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((u) => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`unverified-row-${u.id}`}>
                  <td className="py-2 px-2 text-[var(--cream)]">{u.username}</td>
                  <td className="py-2 px-2 text-[var(--cream-soft)]">
                    {editingId === u.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          className="input-editorial text-xs py-1 px-2 flex-1 min-w-[180px]"
                          data-testid={`unverified-email-input-${u.id}`}
                        />
                        <button onClick={() => saveEmail(u.id)} disabled={busy === u.id}
                          className="text-xs font-meta text-emerald-300 hover:underline disabled:opacity-40 px-1"
                          data-testid={`unverified-email-save-${u.id}`}>Lagre</button>
                        <button onClick={cancelEditEmail}
                          className="text-xs font-meta text-[var(--cream-faint)] hover:underline px-1"
                          data-testid={`unverified-email-cancel-${u.id}`}>Avbryt</button>
                      </div>
                    ) : (
                      u.email
                    )}
                  </td>
                  <td className="py-2 px-2 text-[var(--cream-soft)]">
                    {new Date(u.created_at).toLocaleDateString("nb-NO")}
                    {u.days_since_signup !== null && (
                      <span className="text-[var(--cream-faint)] ml-1">({u.days_since_signup}d)</span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-[var(--cream-soft)]">{u.login_count}</td>
                  <td className="py-2 px-2 text-[var(--cream-faint)] text-xs">
                    {u.last_login_attempt?.ip || "—"}
                  </td>
                  <td className="py-2 px-2">
                    {u.verification_token_state === "valid" && <span className="font-meta text-[10px] text-emerald-300">GYLDIG</span>}
                    {u.verification_token_state === "expired" && <span className="font-meta text-[10px] text-amber-400">UTLØPT</span>}
                    {u.verification_token_state === "missing" && <span className="font-meta text-[10px] text-red-300">MANGLER</span>}
                  </td>
                  <td className="py-2 px-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => resend(u.id)}
                      disabled={busy === u.id}
                      className="text-xs font-meta text-[var(--magenta)] hover:underline disabled:opacity-40 mr-3"
                      data-testid={`unverified-resend-${u.id}`}
                    >
                      Send på nytt
                    </button>
                    <button
                      onClick={() => forceVerify(u.id, u.email)}
                      disabled={busy === u.id}
                      className="text-xs font-meta text-emerald-300 hover:underline disabled:opacity-40 mr-3"
                      data-testid={`unverified-force-${u.id}`}
                    >
                      Force-verifiser
                    </button>
                    {editingId !== u.id && (
                      <button
                        onClick={() => startEditEmail(u.id, u.email)}
                        disabled={busy === u.id}
                        className="text-xs font-meta text-[var(--cream)] hover:underline disabled:opacity-40 mr-3"
                        data-testid={`unverified-edit-email-${u.id}`}
                      >
                        Endre e-post
                      </button>
                    )}
                    {u.login_count === 0 && (
                      <button
                        onClick={() => deleteUser(u.id, u.username)}
                        disabled={busy === u.id}
                        className="text-xs font-meta text-red-400 hover:underline disabled:opacity-40"
                        data-testid={`unverified-delete-${u.id}`}
                      >
                        Slett konto
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ============================================================
// MISLYKKEDE REGISTRERINGER
// ============================================================
const REASON_LABELS = {
  turnstile_failed: "Bot-verifisering feilet",
  invalid_username_chars: "Ugyldige tegn i brukernavn",
  reserved_username: "Reservert brukernavn",
  email_banned: "Bannet e-post",
  email_already_registered: "E-post finnes allerede",
  username_taken: "Brukernavn opptatt",
  under_18_unchecked: "Ikke huket av 18+",
  terms_not_accepted: "Ikke godtatt vilkår",
};

const IN_APP_LABELS = {
  snapchat: "Snapchat",
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
};

function FailedSignupsList() {
  const [data, setData] = useState({ results: [], total: 0, top_reasons: [], top_in_app: [] });
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/failed-signups?days=${days}&limit=300`)
      .then((r) => setData(r.data))
      .catch(() => setData({ results: [], total: 0, top_reasons: [], top_in_app: [] }))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <section data-testid="failed-signups-list">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">Mislykkede registreringer</h2>
        <div className="flex gap-1">
          {[1, 7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`fs-days-${d}`}
              className={`px-3 py-1.5 font-meta text-xs border ${days === d ? "bg-[var(--magenta)] text-black border-[var(--magenta)]" : "border-white/20 text-[var(--cream-soft)] hover:border-white/40"}`}
            >
              {d}D
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="panel-tight p-4">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)]">TOTALT</div>
          <div className="font-fraunces italic text-2xl text-[var(--cream)]">{data.total}</div>
        </div>
        <div className="panel-tight p-4">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-2">TOPP ÅRSAKER</div>
          {data.top_reasons.slice(0, 4).map((r) => (
            <div key={r.reason} className="flex justify-between text-xs text-[var(--cream-soft)] py-0.5">
              <span>{REASON_LABELS[r.reason] || r.reason}</span>
              <span className="font-meta text-[var(--magenta)]">{r.count}</span>
            </div>
          ))}
          {data.top_reasons.length === 0 && <div className="text-xs text-[var(--cream-faint)] italic">Ingen feil registrert</div>}
        </div>
        <div className="panel-tight p-4">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-2">IN-APP NETTLESERE</div>
          {data.top_in_app.map((r) => (
            <div key={r.browser} className="flex justify-between text-xs text-[var(--cream-soft)] py-0.5">
              <span>{IN_APP_LABELS[r.browser] || r.browser}</span>
              <span className="font-meta text-[var(--magenta)]">{r.count}</span>
            </div>
          ))}
          {data.top_in_app.length === 0 && <div className="text-xs text-[var(--cream-faint)] italic">Ingen in-app-feil</div>}
        </div>
      </div>

      {loading && <div className="text-sm text-[var(--cream-soft)]">Laster…</div>}
      {!loading && data.results.length === 0 && (
        <div className="panel-tight p-6 text-center text-[var(--cream-soft)] font-fraunces italic">
          Ingen mislykkede registreringer de siste {days} dagene.
        </div>
      )}
      {!loading && data.results.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] border-b border-white/10">
                <th className="text-left py-2 px-2">TID</th>
                <th className="text-left py-2 px-2">STEG</th>
                <th className="text-left py-2 px-2">ÅRSAK</th>
                <th className="text-left py-2 px-2">E-POST</th>
                <th className="text-left py-2 px-2">BRUKERNAVN</th>
                <th className="text-left py-2 px-2">IP</th>
                <th className="text-left py-2 px-2">ENHET</th>
                <th className="text-left py-2 px-2">IN-APP</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`failed-row-${r.id}`}>
                  <td className="py-2 px-2 text-[var(--cream-faint)] text-xs whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString("nb-NO")}
                  </td>
                  <td className="py-2 px-2 text-[var(--cream)] text-xs">{r.step}</td>
                  <td className="py-2 px-2 text-[var(--cream-soft)] text-xs">{REASON_LABELS[r.reason] || r.reason}</td>
                  <td className="py-2 px-2 text-[var(--cream-soft)] text-xs">{r.email_masked || "—"}</td>
                  <td className="py-2 px-2 text-[var(--cream-soft)] text-xs">{r.username_attempted || "—"}</td>
                  <td className="py-2 px-2 text-[var(--cream-faint)] text-xs">{r.ip}</td>
                  <td className="py-2 px-2 text-[var(--cream-faint)] text-xs">{r.device}</td>
                  <td className="py-2 px-2 text-[var(--cream-soft)] text-xs">
                    {r.in_app_browser ? (IN_APP_LABELS[r.in_app_browser] || r.in_app_browser) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

